import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useWallet } from '../contexts/WalletContext';
import { CreateMatchModal } from '../components/CreateMatchModal';

interface Match {
  matchId: string;
  title: string;
  sideA: string;
  sideB: string;
  matchTime: string;
  venue: string;
  sportType: string;
  status: string;
  totalPool: string;
  activeDuels: number;
  sideAPool: string;
  sideBPool: string;
  judgeAddress: string;
  creatorAddress: string;
  createdAt: string;
}

interface JudgeStats {
  address: string;
  totalMatches: number;
  completedMatches: number;
  upcomingMatches: number;
  liveMatches: number;
  totalVolume: string;
}

export default function JudgePanel() {
  const { address: walletAddress, isConnected } = useWallet();
  const navigate = useNavigate();
  const [matches, setMatches] = useState<Match[]>([]);
  const [stats, setStats] = useState<JudgeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<'all' | 'active'>('active');
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    if (isConnected && walletAddress) {
      fetchData();
    }
  }, [isConnected, walletAddress, filter]);

  const fetchData = async () => {
    setLoading(true);
    setError('');

    try {
      // Get Judge's matches
      const hasActiveDuels = filter === 'active' ? 'true' : 'false';
      const matchesResponse = await fetch(
        `/api/judge/matches?judgeAddress=${walletAddress}&hasActiveDuels=${hasActiveDuels}`
      );
      const matchesData = await matchesResponse.json();

      if (!matchesData.success) {
        throw new Error(matchesData.message || 'Failed to fetch matches');
      }

      setMatches(matchesData.data.matches);

      // Get statistics
      const statsResponse = await fetch(`/api/judge/stats/${walletAddress}`);
      const statsData = await statsResponse.json();

      if (statsData.success) {
        setStats(statsData.data);
      }
    } catch (err: any) {
      console.error('Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFinishClick = (match: Match) => {
    navigate(`/judge/finish/${match.matchId}`);
  };

  const handleSuccess = () => {
    fetchData(); // Refresh data
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'upcoming':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">Upcoming</span>;
      case 'live':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">Live</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">Completed</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">{status}</span>;
    }
  };

  if (!isConnected || !walletAddress) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">🏛️ Judge Panel</h2>
          <p className="text-gray-600 mb-6">Please connect your wallet to access the Judge Panel.</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 max-w-md mx-auto">
            <p className="text-yellow-800">
              This panel is for authorized judges to create and manage matches.
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  if (loading && !matches.length) {
    return (
      <Layout>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-xl text-gray-600">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <>
    <Layout>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">🏛️ Judge Panel</h1>
        <p className="text-gray-600">Create matches and manage settlements</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-indigo-600">{stats.totalMatches}</div>
            <div className="text-sm text-gray-600">Total Matches</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-green-600">{stats.completedMatches}</div>
            <div className="text-sm text-gray-600">Completed</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-blue-600">{stats.upcomingMatches}</div>
            <div className="text-sm text-gray-600">Upcoming</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-red-600">{stats.liveMatches}</div>
            <div className="text-sm text-gray-600">Live</div>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-2xl font-bold text-purple-600">{parseFloat(stats.totalVolume).toFixed(0)}</div>
            <div className="text-sm text-gray-600">Total Volume (CHNX)</div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <button
          onClick={() => {
            console.log('[JudgePanel] Create New Match button clicked');
            setShowCreateModal(true);
          }}
          className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 font-semibold flex items-center gap-2 shadow-md"
        >
          <span className="text-xl">➕</span>
          Create New Match
        </button>

        <div className="flex gap-2">
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-md font-medium ${
              filter === 'active'
                ? 'bg-green-100 text-green-800 border-2 border-green-500'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Awaiting Judgment ({matches.filter(m => m.activeDuels > 0 && m.status !== 'completed').length})
          </button>
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-md font-medium ${
              filter === 'all'
                ? 'bg-indigo-100 text-indigo-800 border-2 border-indigo-500'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            All Matches
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
          {error}
        </div>
      )}

      {/* Matches List */}
      <div className="space-y-4">
        {matches.length === 0 ? (
          <div className="bg-gray-50 rounded-lg p-12 text-center">
            <p className="text-gray-600 text-lg mb-4">
              {filter === 'active' 
                ? 'No matches awaiting judgment' 
                : 'No matches created yet'}
            </p>
            <p className="text-gray-500 text-sm mb-6">
              {filter === 'active'
                ? 'Matches with active duels will appear here.'
                : 'Create your first match to get started.'}
            </p>
            {filter === 'all' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700 font-medium"
              >
                Create Match
              </button>
            )}
          </div>
        ) : (
          matches.map((match) => (
            <div
              key={match.matchId}
              className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                {/* Match Info */}
                <div className="flex-1">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-xl font-bold text-gray-900">{match.title}</h3>
                        {getStatusBadge(match.status)}
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-2">
                        <span>📅 {new Date(match.matchTime).toLocaleString()}</span>
                        <span>📍 {match.venue}</span>
                        <span>🏸 {match.sportType}</span>
                      </div>
                      <div className="flex items-center gap-6 text-sm">
                        <div>
                          <span className="font-semibold text-gray-700">Side A:</span>{' '}
                          <span className="text-gray-900">{match.sideA}</span>
                        </div>
                        <div>
                          <span className="font-semibold text-gray-700">Side B:</span>{' '}
                          <span className="text-gray-900">{match.sideB}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Pool Info */}
                  <div className="flex flex-wrap gap-4 mt-4">
                    <div className="bg-purple-50 rounded-lg px-4 py-2">
                      <div className="text-xs text-purple-600 font-medium">Total Pool</div>
                      <div className="text-lg font-bold text-purple-700">
                        {parseFloat(match.totalPool).toFixed(2)} CHNX
                      </div>
                    </div>
                    <div className="bg-blue-50 rounded-lg px-4 py-2">
                      <div className="text-xs text-blue-600 font-medium">Active Duels</div>
                      <div className="text-lg font-bold text-blue-700">{match.activeDuels}</div>
                    </div>
                    <div className="bg-green-50 rounded-lg px-4 py-2">
                      <div className="text-xs text-green-600 font-medium">Side A Pool</div>
                      <div className="text-lg font-bold text-green-700">
                        {parseFloat(match.sideAPool).toFixed(2)}
                      </div>
                    </div>
                    <div className="bg-orange-50 rounded-lg px-4 py-2">
                      <div className="text-xs text-orange-600 font-medium">Side B Pool</div>
                      <div className="text-lg font-bold text-orange-700">
                        {parseFloat(match.sideBPool).toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col gap-2 lg:w-48">
                  <Link
                    to={`/match/${match.matchId}`}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 text-center font-medium"
                  >
                    View Details
                  </Link>
                  {match.status !== 'completed' && match.activeDuels > 0 && (
                    <button
                      onClick={() => handleFinishClick(match)}
                      className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 font-semibold flex items-center justify-center gap-2"
                    >
                      <span>🏆</span>
                      Finish Match
                    </button>
                  )}
                  {match.status === 'completed' && (
                    <div className="px-4 py-2 bg-gray-50 text-gray-500 rounded-md text-center text-sm">
                      ✅ Settled
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

    </Layout>
      
      {/* Modals - rendered outside Layout */}
      <CreateMatchModal
        isOpen={showCreateModal}
        onClose={() => {
          console.log('[JudgePanel] CreateMatchModal onClose called');
          console.trace('[JudgePanel] onClose call stack');
          setShowCreateModal(false);
        }}
        onSuccess={handleSuccess}
      />
    </>
  );
}

